import React, { useState, useEffect } from "react";
import { useAuth } from "components/ui/navigationGuard";
import AdminNavigation from "layouts/adminNavigation";
import EmployeeNavigation from "layouts/employeeNavigation";
import axiosInstance from "utils/axiosInstance";
import SalaryModal from "./salaryModal";
import SalaryTable from "./salaryTable";
import Button from "components/ui/button";

const SalaryManagement = () => {
  const { user } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [salaries, setSalaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSalary, setSelectedSalary] = useState(null);
  const [monthFilter, setMonthFilter] = useState(new Date().toISOString().slice(0, 7));
  const [salaryDefaults, setSalaryDefaults] = useState({});

  // -------------------------
  // Load salaries based on role and selected month
  // -------------------------
  const loadSalaries = async () => {
    setLoading(true);
    try {
      if (user.role === "admin") {
        const response = await axiosInstance.get("/salary");
        setSalaries(response.data);
      } else {
        const response = await axiosInstance.get(`/salary/${user.id}/${monthFilter}`);
        setSalaries([response.data]);
      }
    } catch (err) {
      console.error(err);
      setSalaries([]);
    }
    setLoading(false);
  };

  // -------------------------
  // Fetch defaults for creating salary
  // -------------------------
  const loadDefaults = async (employeeType) => {
    try {
      const response = await axiosInstance.get(`/salary/defaults/${employeeType}`);
      setSalaryDefaults(response.data);
    } catch (err) {
      console.error("Error fetching salary defaults:", err);
      setSalaryDefaults({});
    }
  };

  // -------------------------
  // Effects
  // -------------------------
  useEffect(() => {
    loadSalaries();
  }, [user, monthFilter]);

  // -------------------------
  // Handlers
  // -------------------------
  const handleEdit = (salary) => {
    setSelectedSalary(salary);
    setModalOpen(true);
  };

  const handleCalculate = async (salary) => {
    try {
      await axiosInstance.post(`/salary/calculate/${salary.employee_id}/${salary.month}`);
      loadSalaries();
    } catch (err) {
      console.error(err);
    }
  };

  const handleCreate = async () => {
    setSelectedSalary(null);
    await loadDefaults(user?.employee_type);
    setModalOpen(true);
  };

  const handleSave = async (formData) => {
    try {
      if (selectedSalary) {
        await axiosInstance.put(`/salary/${selectedSalary.id}`, formData);
      } else {
        await axiosInstance.post("/salary/", {
          ...formData,
          employee_id: user?.id,
          employee_type: user?.employee_type,
        });
      }
      setModalOpen(false);
      setSelectedSalary(null);
      loadSalaries();
    } catch (err) {
      console.error(err);
    }
  };

  const handleToggleCollapse = () => setIsCollapsed(!isCollapsed);

  // -------------------------
  // Loading state
  // -------------------------
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        {user?.role === "admin" ? <AdminNavigation /> : <EmployeeNavigation />}
        <div className="pt-16 flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            <p className="text-sm text-muted-foreground">Loading salary data...</p>
          </div>
        </div>
      </div>
    );
  }

  // -------------------------
  // Main render
  // -------------------------
  return (
    <div className="min-h-screen bg-background">
      {user.role === "admin" ? (
        <AdminNavigation user={user} isCollapsed={isCollapsed} onToggleCollapse={handleToggleCollapse} />
      ) : (
        <EmployeeNavigation user={user} isCollapsed={isCollapsed} onToggleCollapse={handleToggleCollapse} />
      )}

      <main className={`pt-16 transition-all duration-200 ease-in-out ${isCollapsed ? "lg:pl-16" : "lg:pl-64"}`}>
        <div className="p-4 sm:p-6 lg:p-8 space-y-8">
          <div className="flex justify-between mb-4">
            <h1 className="text-xl font-bold">Salary Management</h1>
            <div className="flex space-x-2">
              <input
                type="month"
                value={monthFilter}
                onChange={(e) => setMonthFilter(e.target.value)}
                className="border p-1 rounded"
              />
              {user.role === "admin" && <Button onClick={handleCreate}>Create Salary</Button>}
            </div>
          </div>

          <SalaryTable
            salaries={salaries}
            userRole={user.role}
            onEdit={handleEdit}
            onCalculate={handleCalculate}
            salaryDefaults={salaryDefaults}
          />

          {modalOpen && (
            <SalaryModal
              isOpen={modalOpen}
              salary={selectedSalary ? selectedSalary : salaryDefaults} // use defaults if creating
              isCreate={!selectedSalary}
              onClose={() => setModalOpen(false)}
              onSave={handleSave}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default SalaryManagement;
